import React, {
	createContext,
	useContext,
	useImperativeHandle,
	useMemo,
} from 'react';
import {QueryClientProvider} from '@tanstack/react-query';
import Permissions from '@netx/core-attribute-permission/containers/Permissions';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {modalManagerStore} from '@netx/core-components/ModalManager';
import {
	type IContextChildren,
	type IContextRef,
	renderChildrenOrChildrenFunction,
} from '@netx/core-next';
import {queryClient} from '@netx/core-next/lib';
import {useAttributes} from '../hooks/useAttributes';

export type AttributeProviderRef = IContextRef<AttributeContextType>;

export type AttributeContextType = ReturnType<typeof useAttributes> & {
	setPermissionEdit: (id: number | false) => void;
};

const AttributeContext = createContext<AttributeContextType>({
	data: [],
	attributeIds: [],
	attributesTrackingHistory: [],
	trackingHistory: false,
	attributesWorking: {},
	loading: false,
	lookups: {
		keys: [],
		targets: [],
	},
	hasMandatoryUserDefinedTemplates: false,
	metadataLookupsEnabled: false,
	total: 0,
	getAll: () => [],
	getAllAsObject: () => ({}),
	getById: () => undefined,
	getByIds: () => [],
	getByIdsAsObject: () => ({}),
	getByName: () => undefined,
	getByNames: () => [],
	getByType: () => [],
	getDefaultValues: () => ({}),
	getLabelById: () => '',
	lookup: async () => ({}),
	searchValue: (id: number, value: string) => {},
	setPermissionEdit: () => {},
});

export const useAttributeProvider = () => useContext(AttributeContext);

export type AttributeProviderProps = Parameters<typeof useAttributes>[0] & {
	ref?: AttributeProviderRef;
	children: IContextChildren<AttributeContextType>;
};

const AttributeProviderInner = (
	{children, ...props}: AttributeProviderProps,
	ref: AttributeProviderRef,
) => {
	const attributes = useAttributes(props);

	const setPermissionEdit = useConstCallback((id: number | false) => {
		if (id === false) return modalManagerStore.hide('Permissions');

		modalManagerStore.show('Permissions', Permissions, {
			templateId: id,
		});
	});

	const contextValue = useMemo(
		() => ({
			...attributes,
			setPermissionEdit,
		}),
		[attributes],
	);

	useImperativeHandle(ref, () => contextValue, [contextValue]);

	return (
		<AttributeContext.Provider value={contextValue}>
			{renderChildrenOrChildrenFunction(children, contextValue)}
		</AttributeContext.Provider>
	);
};

export const AttributeProviderPrimitive = React.forwardRef<
	AttributeContextType,
	AttributeProviderProps
>(AttributeProviderInner);

export const AttributeProvider = React.forwardRef<
	AttributeContextType,
	AttributeProviderProps
>((props: AttributeProviderProps, ref) => {
	return (
		<QueryClientProvider client={queryClient}>
			<AttributeProviderPrimitive {...props} ref={ref} />
		</QueryClientProvider>
	);
});

export default AttributeProvider;
